import { IDatasource, IGetRowsParams, SortModelItem } from "ag-grid-community";
import { RefObject } from "react";
import { AgGridReact } from "ag-grid-react";
import handleGridPaginatedResponse from "./GridPaginatedResponseHandler";
import { GridPaginatedResponse } from "@/interfaces/GridPaginatedResponse";
import SearchGridSchema from "../../interfaces/SearchGridSchema";
import QueryParams from "../../interfaces/QueryParams";
import api from "../api";
import { ParsedFilter } from "./FilterModelFilters";
import { AxiosError } from "axios";

class SearchGridServerSideDatasource implements IDatasource {
  private _searchParams: Partial<QueryParams>;
  private readonly _gridRef: RefObject<AgGridReact<SearchGridSchema>>;
  private readonly _onResultsFound: (count: number) => void;

  constructor(
    searchParmas: Partial<QueryParams>,
    gridRef: RefObject<AgGridReact<SearchGridSchema>>,
    onResultsFound: (count: number) => void,
  ) {
    this._searchParams = searchParmas;
    this._gridRef = gridRef;
    this._onResultsFound = onResultsFound;
  }

  set searchParams(value: Partial<QueryParams>) {
    this._searchParams = value;
  }

  // Implementation of the getRows method from IServerSideDatasource
  getRows(params: IGetRowsParams) {
    /* Extract necessary parameters from params
      ### IGetRowsParams interface doesn't include type for filterModel
    */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { startRow, endRow, sortModel, filterModel } = params;
    const parsedFilterModel = filterModel as ParsedFilter;

    // Preparing query parameters for pagination, sorting, and filtering
    const queryBody = {
      startRow: startRow,
      endRow: endRow,
      filter: parsedFilterModel,
      ...this.getQueryParams(),
      ...this.parseSortModel(sortModel),
    };

    this._gridRef.current?.api.setGridOption("loading", true);

    // Make an axios call to fetch data
    api
      .post<GridPaginatedResponse<SearchGridSchema>>("/search", queryBody, {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        this._gridRef.current?.api.setGridOption("loading", false);
        if (response.status === 204) {
          this._onResultsFound(0);
          return this._gridRef.current?.api.showNoRowsOverlay();
        }
        const totalRows = response.data.responseObject.total;
        this._onResultsFound(totalRows);
        const responseHandler = handleGridPaginatedResponse(
          this._gridRef,
          params,
        );
        responseHandler(response);
      })
      .catch((error: AxiosError) => {
        this._gridRef.current?.api.setGridOption("loading", false);
        if (error.status == 404) {
          this._onResultsFound(0);
          return this._gridRef.current?.api.showNoRowsOverlay();
        }
        this._onResultsFound(0);
        console.error("Error while fetching data from server:", error);
        params.failCallback();
      });
  }

  getQueryParams() {
    if (this._searchParams) {
      return this._searchParams;
    }
    return {};
  }

  parseSortModel(sortModel: SortModelItem[]) {
    if (sortModel && sortModel.length > 0) {
      const sortRules = sortModel.map((model) => {
        return { colId: model.colId, sort: model.sort };
      });

      return { sort: sortRules };
    }
    return {};
  }
}

export default SearchGridServerSideDatasource;
